import { useMutation } from '@apollo/client';
import * as icons from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TooltipSquareIconButton } from 'components/common/icon/tooltip-icon-button';
import { LoadingBarOrEmptySpace } from 'components/common/loading/loading-bar-or-empty-space';
import { SyncHighLevelDialog } from 'components/views/conversation/sync-highlevel-dialog';
import moment from 'moment-timezone';
import { useAuthContext } from 'providers/auth';
import React, { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  ConversationVariables,
  Conversation_externalLead_conversation,
  Conversation_externalLead_scheduledOutreach,
} from 'types/apollo/Conversation';
import { SendSms, SendSmsVariables } from 'types/apollo/SendSms';
import { formatDate } from 'utils/date';
import { InterveneButton, NewMessageField, ScheduledTimePicker } from './compose-form';
import { SendSmsMutation } from './conversation.graphql';
import { getDefaultTime } from './conversation.util';
import { buildDatadogLinks } from './datadog-links';
import { DatadogMessageMenu } from './datadog-message-menu';
import {
  AktifyIcon2,
  ChatBubble,
  ChatMessage,
  InboundCallStatus,
  InboundSmsAndMedia,
  LeadChatCircle,
  LeadEventCard,
  LinkClickEventCard,
  OutboundCallStatus,
  Recording,
  ScheduledPreviewBubble,
  Timestamp,
} from './message-types';

// ============================================================================
// Styled Components
// ============================================================================

const ChatContainer = styled(Card)(() => ({
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: '20px',
  borderRadius: '0',
  width: '100%',
  backgroundColor: 'inherit',
  overflowY: 'auto',
  flexGrow: 1,
  flexShrink: 1,
  minHeight: 0,
}));

const ChatContent = styled(CardContent)(() => ({
  gap: '30px',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '1100px',
  marginLeft: 'auto',
  marginRight: 'auto',
}));

// ============================================================================
// Component
// ============================================================================

export const ChatConversation: FC<{
  messages: Conversation_externalLead_conversation[] | null;
  loading: boolean;
  leadInitials: string | null;
  leadTimeZone: string;
  onExport: () => Promise<any>;
  campaignId: string;
  phone?: string;
  fetchConversation: (variables?: ConversationVariables) => Promise<any>;
  scheduledOutreach?: Conversation_externalLead_scheduledOutreach[];
  repostedAt?: Date | null;
}> = ({
  messages,
  loading,
  leadInitials,
  leadTimeZone,
  onExport,
  campaignId,
  phone,
  fetchConversation,
  scheduledOutreach = [],
  repostedAt,
}) => {
  const { isInternal } = useAuthContext();
  const [newMessage, setNewMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [replyingToPhoneAndCamp, setReplyingToPhoneAndCamp] = useState<
    { phone: string; campaignId: string } | false
  >(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [lastConfirmedMessage, setLastConfirmedMessage] = useState('');
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [showInterveneMenu, setShowInterveneMenu] = useState(false);
  const [interveneHours, setInterveneHours] = useState<number | null>(null);
  const interveneMenuRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const repostedDate = repostedAt ?? null;

  const repostMarkerIndex = useMemo(() => {
    if (!repostedDate) return -1;
    return (
      messages?.findIndex((m) => m.time && new Date(m.time).getTime() > repostedDate.getTime()) ??
      -1
    );
  }, [messages, repostedDate]);

  const [sendSms] = useMutation<SendSms, SendSmsVariables>(SendSmsMutation);

  const handleScheduleToggle = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!showSchedulePicker) {
      const nextDay = getDefaultTime();
      setScheduledTime(nextDay.toDate());
    } else {
      setScheduledTime(null);
    }
    setShowSchedulePicker(!showSchedulePicker);
    setShowInterveneMenu(false);
  };

  const handleInterveneClear = () => {
    setInterveneHours(null);
    setScheduledTime(null);
  };

  const handleInterveneSelect = (hours: number) => {
    setInterveneHours(hours);
    setShowInterveneMenu(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !campaignId || !phone) return;

    if (scheduledTime) {
      const now = moment();
      const scheduledMoment = moment(scheduledTime).tz(leadTimeZone, true);

      if (scheduledMoment.isBefore(now)) {
        alert('Cannot schedule a message in the past. Please select a future time.');
        return;
      }
    }

    if (!confirmSend || lastConfirmedMessage !== newMessage) {
      setConfirmSend(true);
      setLastConfirmedMessage(newMessage);
      return;
    }

    try {
      setSendingSms(true);
      await sendSms({
        variables: {
          input: {
            body: newMessage,
            campaignId,
            leadPhoneNumber: phone,
            ...(scheduledTime && {
              when: moment(scheduledTime).tz(leadTimeZone, true).toISOString(),
            }),
            ...(interveneHours != null && {
              interveneUntil: moment().add(interveneHours, 'hours').toISOString(),
            }),
          },
        },
      });

      if (scheduledTime) {
        await fetchConversation({
          campaignId,
          phone,
          includeCampaignName: false,
        });
      }

      setNewMessage('');
      setConfirmSend(false);
      setLastConfirmedMessage('');
      setReplyingToPhoneAndCamp(false);
      setScheduledTime(null);
      setShowSchedulePicker(false);
      setInterveneHours(null);
    } catch (error) {
      console.error('Error sending SMS:', error);
    } finally {
      setSendingSms(false);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [messages]);

  // Reset confirmation state when message changes
  useEffect(() => {
    if (confirmSend && newMessage !== lastConfirmedMessage) {
      setConfirmSend(false);
    }
  }, [newMessage, lastConfirmedMessage]);

  return (
    <Stack
      direction="column"
      width="100%"
      height="100%"
      sx={{ display: 'flex', position: 'relative' }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
        <LoadingBarOrEmptySpace height="5px" loading={loading} />
      </Box>

      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <ChatContainer>
          <ChatContent>
            {messages?.map((message, index) => {
              const linkClickedDetails =
                message.leadEvent &&
                message.leadEvent.type == 'shortio' &&
                message.leadEvent.events?.[0].linkClickedDetails
                  ? message.leadEvent.events?.[0].linkClickedDetails
                  : null;

              // Aktify staff only — customers share this view and must never see
              // our internal Datadog tooling. Inbound only: the lead's reply is
              // what triggers the backend run. Null (not internal, too old, no
              // env) means no menu and no reserved padding.
              const datadogLinks =
                isInternal && message.inboundSms ? buildDatadogLinks(phone, message.time) : null;

              const alignLeft =
                !!linkClickedDetails ||
                message.inboundSms ||
                message.inboundCall ||
                message.recording?.direction == 'inbound'
                  ? 'left'
                  : 'right';

              return (
                <Fragment key={message.time}>
                  {index === repostMarkerIndex && repostedDate && (
                    <Box
                      sx={{
                        width: '100%',
                        backgroundColor: '#e3f2fd',
                        color: '#0d47a1',
                        textAlign: 'center',
                        fontWeight: 500,
                        py: 1.2,
                        px: 2,
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                      }}
                    >
                      Lead was reposted on {formatDate(repostedDate!.toISOString())}
                    </Box>
                  )}

                  <ChatMessage className={alignLeft}>
                    {message.inboundSms && (
                      <Box display="flex" alignItems="center">
                        {leadInitials && <LeadChatCircle>{leadInitials}</LeadChatCircle>}
                        <InboundSmsAndMedia
                          inboundSms={message.inboundSms}
                          menu={datadogLinks && <DatadogMessageMenu links={datadogLinks} />}
                        />
                      </Box>
                    )}
                    {message.outboundSms && (
                      <Box display="flex" alignItems="center">
                        <ChatBubble className="right">
                          <Stack gap={1}>
                            {message.outboundSms.mediaUrl && (
                              <Box
                                component="img"
                                src={message.outboundSms.mediaUrl}
                                alt="MMS attachment"
                                borderRadius="5px"
                                minWidth="300px"
                                maxWidth="100%"
                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                  const parent = e.currentTarget.parentElement;
                                  if (parent) {
                                    const fallback = parent.querySelector(
                                      '[data-media-url-fallback]',
                                    ) as HTMLElement | null;
                                    if (fallback) fallback.style.display = 'flex';
                                  }
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                            {message.outboundSms.mediaUrl && (
                              <Box
                                data-media-url-fallback
                                sx={{
                                  display: 'none',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: 'error.main',
                                }}
                              >
                                <icons.BrokenImage fontSize="small" />
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: 'inherit',
                                    maxWidth: 300,
                                  }}
                                >
                                  {message.outboundSms.mediaUrl}
                                </Typography>
                              </Box>
                            )}
                            {message.outboundSms.body}
                          </Stack>
                        </ChatBubble>
                        <AktifyIcon2 />
                      </Box>
                    )}
                    {message.inboundCall && (
                      <Box display="flex" alignItems="center">
                        <InboundCallStatus callEvent={message.inboundCall} />
                      </Box>
                    )}
                    {message.outboundCall && (
                      <Box display="flex" alignItems="center">
                        <OutboundCallStatus callEvent={message.outboundCall} />
                      </Box>
                    )}
                    {message.recording && (
                      <Box paddingLeft="30px" paddingRight="48px" minWidth="400px" width="100%">
                        <Recording recording={message.recording} />
                      </Box>
                    )}
                    {message.leadEvent && message.leadEvent.type == 'gohighlevel' && (
                      <Box paddingLeft="30px" paddingRight="48px" minWidth="400px" width="100%">
                        <LeadEventCard leadEvent={message.leadEvent} leadTimeZone={leadTimeZone} />
                      </Box>
                    )}
                    {linkClickedDetails && message.leadEvent?.events && (
                      <Box display="flex" alignItems="flex-start" width="100%">
                        {leadInitials && (
                          <LeadChatCircle sx={{ mt: 1.5, flexShrink: 0 }}>
                            {leadInitials}
                          </LeadChatCircle>
                        )}
                        <LinkClickEventCard
                          linkClickedDetails={linkClickedDetails}
                          leadTimeZone={leadTimeZone}
                        />
                      </Box>
                    )}
                    <Typography variant="body2" color="textSecondary" paddingX="38px">
                      <Timestamp>
                        {message.outboundSms?.status ? (
                          <>{message.outboundSms.status} &bull; </>
                        ) : null}
                        {formatDate(message.time)}
                      </Timestamp>
                    </Typography>
                  </ChatMessage>
                </Fragment>
              );
            })}
            {(scheduledOutreach ?? [])
              .slice()
              .sort((a, b) => Date.parse(a.when) - Date.parse(b.when))
              .map((it) => (
                <ScheduledPreviewBubble key={`${it.kind}-${it.when}`} item={it} tz={leadTimeZone} />
              ))}

            {!loading && messages && phone && messages.length > 0 ? (
              <ChatMessage className="right">
                <ChatBubble
                  className="right"
                  sx={{
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    display: 'flex',
                    gap: 1,
                    marginRight: '36px',
                  }}
                >
                  <TooltipSquareIconButton
                    tooltip="Refresh conversation"
                    onClick={() => fetchConversation({ campaignId, phone })}
                  >
                    <icons.Refresh />
                  </TooltipSquareIconButton>
                  {isInternal && (
                    <SyncHighLevelDialog
                      campaignId={campaignId}
                      phone={phone}
                      fetchConversation={() => fetchConversation()}
                    />
                  )}
                  {isInternal &&
                    (!replyingToPhoneAndCamp ||
                      replyingToPhoneAndCamp.phone != phone ||
                      replyingToPhoneAndCamp.campaignId != campaignId) && (
                      <TooltipSquareIconButton
                        tooltip="Reply"
                        onClick={() => {
                          setReplyingToPhoneAndCamp({ phone, campaignId });
                        }}
                      >
                        <icons.Reply />
                      </TooltipSquareIconButton>
                    )}
                  {isInternal && (
                    <TooltipSquareIconButton tooltip="See on Chat" onClick={onExport}>
                      <icons.QuestionAnswer />
                    </TooltipSquareIconButton>
                  )}
                </ChatBubble>
              </ChatMessage>
            ) : null}
            <div ref={messagesEndRef} style={{ height: '20px' }} />
          </ChatContent>
        </ChatContainer>
        {replyingToPhoneAndCamp &&
          replyingToPhoneAndCamp.phone == phone &&
          replyingToPhoneAndCamp.campaignId == campaignId && (
            <Box display="flex" flexDirection="column" sx={{ backgroundColor: '#e8f5e9' }}>
              {showSchedulePicker && (
                <ScheduledTimePicker
                  scheduledTime={scheduledTime}
                  setScheduledTime={setScheduledTime}
                  leadTimeZone={leadTimeZone}
                />
              )}
              <Box display="flex" padding={2} gap={1}>
                <NewMessageField
                  value={newMessage}
                  onChange={setNewMessage}
                  sendingSms={sendingSms}
                  loading={loading}
                  phone={phone}
                />

                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="medium"
                    onClick={handleScheduleToggle}
                    disabled={sendingSms || loading || !phone}
                    color="primary"
                    sx={{
                      border: '1px solid rgba(25, 118, 210, 0.5)',
                      borderRadius: '4px',
                      height: '36px',
                      width: '36px',
                      '&:hover': {
                        border: '1px solid #1976d2',
                        bgcolor: 'rgba(25,118,210,0.37)',
                      },
                    }}
                  >
                    {showSchedulePicker ? (
                      <icons.EventBusy />
                    ) : scheduledTime ? (
                      <icons.EventAvailable />
                    ) : (
                      <icons.Schedule />
                    )}
                  </IconButton>

                  <InterveneButton
                    onClickInterveneButton={() => setShowInterveneMenu(!showInterveneMenu)}
                    sendingSms={sendingSms}
                    loading={loading}
                    phone={phone}
                    interveneHours={interveneHours}
                    onClearIntervene={handleInterveneClear}
                    showInterveneMenu={showInterveneMenu}
                    menuRef={interveneMenuRef}
                    createButton={(hours) => (
                      <Button
                        key={hours}
                        onClick={() => handleInterveneSelect(hours)}
                        sx={{
                          justifyContent: 'flex-start',
                          padding: '8px 16px',
                          width: '100%',
                          textAlign: 'left',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.18)',
                          },
                          bgcolor:
                            interveneHours === hours ? 'rgba(25, 118, 210, 0.2)' : 'transparent',
                        }}
                      >
                        {hours} {hours === 1 ? 'hour' : 'hours'}
                      </Button>
                    )}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={
                      sendingSms ||
                      loading ||
                      !phone ||
                      (scheduledTime
                        ? moment(scheduledTime).tz(leadTimeZone, true).isBefore(moment())
                        : false) ||
                      !newMessage.trim()
                    }
                    startIcon={sendingSms ? <CircularProgress size={20} color="inherit" /> : null}
                    endIcon={confirmSend ? <icons.Send /> : null}
                    sx={{ minWidth: '100px', height: '36px', whiteSpace: 'nowrap' }}
                  >
                    {sendingSms
                      ? 'Sending...'
                      : confirmSend
                      ? 'Confirm'
                      : scheduledTime
                      ? 'Schedule'
                      : 'Send'}
                  </Button>
                </Stack>
              </Box>
            </Box>
          )}
      </Box>
    </Stack>
  );
};
